import React, { useState, useRef, useEffect, useCallback } from "react";
import { getDataSubmit } from "utils/firebaseFunction";
import correctSound from "./correct-answer.mp3";
import wrongSound from "./incorrect1.mp3";
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';

export default function Quizdata({ id }) {
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [quizFinished, setQuizFinished] = useState(false);
  const [score, setScore] = useState(0);
  const [reviewMode, setReviewMode] = useState(false);
  const questionRefs = useRef([]);

  // إنشاء مراجع لعناصر الصوت
  const correctAudioRef = useRef(new Audio(correctSound));
  const wrongAudioRef = useRef(new Audio(wrongSound));

  // تعريف timer باستخدام useRef
  const timerRef = useRef(null);

  // تحميل الملفات الصوتية مسبقاً
  useEffect(() => {
    correctAudioRef.current.load();
    wrongAudioRef.current.load();
  }, []);

  // حساب عدد الإجابات الصحيحة
  const calculateCorrectAnswers = useCallback(() => {
    if (!quiz || !quiz.questions) return 0;
    
    let correctCount = 0;
    quiz.questions.forEach((question, index) => {
      const userAnswer = selectedAnswers[index];
      if (userAnswer === undefined) return;

      if (userAnswer === question.correctAnswer) {
        correctCount++;
      }
    });
    return correctCount;
  }, [quiz, selectedAnswers]);

  // تحميل بيانات الاختبار
  useEffect(() => {
    const loadQuizData = async () => {
      try {
        const { quiz, timeLimit } = await getDataSubmit(id);
        if (!quiz || !quiz.questions || quiz.questions.length === 0) {
          setError("No quiz data available");
        } else {
          setQuiz(quiz);
          setTimeLeft(timeLimit || 60);
        }
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    loadQuizData();
  }, [id]);

  // إدارة المؤقت
  useEffect(() => {
    if (timeLeft === 0) {
      handleNextQuestion();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, currentQuestionIndex]);

  // إيقاف المؤقت عند ترك الصفحة
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (correctAudioRef.current) correctAudioRef.current.pause();
      if (wrongAudioRef.current) wrongAudioRef.current.pause();
      if (timerRef.current) clearInterval(timerRef.current);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // الانتقال إلى السؤال التالي
  const handleNextQuestion = () => {
    if (!quiz || !quiz.questions) return;
  
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      const nextQuestionTimeLimit = quiz.questions[currentQuestionIndex + 1]?.timeLimit || 60;
      setTimeLeft(nextQuestionTimeLimit);
    } else {
      finishQuiz();
    }
  };
  // إنهاء الاختبار
  const finishQuiz = () => {
    setQuizFinished(true);
    calculateScore();
    if (timerRef.current) clearInterval(timerRef.current); // إيقاف المؤقت
  };

  // حساب النتيجة
  const calculateScore = () => {
    const correctAnswers = calculateCorrectAnswers();
    setScore(correctAnswers);
  };

  // الانتقال إلى وضع المراجعة
  const handleReview = () => {
    setReviewMode(!reviewMode);
    setCurrentQuestionIndex(0);
  };

  // إعادة تقديم الاختبار
  const handleRetakeQuiz = () => {
    setSelectedAnswers({});
    setCurrentQuestionIndex(0);
    setQuizFinished(false);
    setReviewMode(false);
    // التعديل هنا: استخدام الوقت الافتراضي للاختبار
    const initialTime = quiz.questions[0]?.timeLimit || quiz.timeLimit || 60;
    setTimeLeft(initialTime);
  };

  // معالجة اختيار الإجابة
  const handleAnswerSelection = (questionIndex, optionIndex) => {
    if (selectedAnswers[questionIndex] !== undefined) return;

    setSelectedAnswers((prev) => ({
      ...prev,
      [questionIndex]: optionIndex,
    }));

    // إيقاف أي صوت يعمل حالياً
    correctAudioRef.current.pause();
    wrongAudioRef.current.pause();

    // إعادة تعيين الوقت الحالي للصوت
    correctAudioRef.current.currentTime = 0;
    wrongAudioRef.current.currentTime = 0;

    // تشغيل الصوت المناسب
    const currentQuestion = quiz.questions[questionIndex];
    if (optionIndex === currentQuestion.correctAnswer) {
      correctAudioRef.current.play(); // تشغيل صوت الإجابة الصحيحة
    } else {
      wrongAudioRef.current.play(); // تشغيل صوت الإجابة الخاطئة
    }

    // إيقاف المؤقت عند اختيار الإجابة
    setTimeLeft(2);
  };

  if (loading) {
    return (
      <div className="quiz-container">
        <LoadingSpinner size="large" text="Loading quiz data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button 
          className="Btn" 
          onClick={() => window.location.href = '/'}
        >
          Return to Home
        </button>
      </div>
    );
  }

  if (!quiz || !quiz.questions || quiz.questions.length === 0) return <div>No quiz data available</div>;

  const currentQuestion = quiz.questions[currentQuestionIndex];
  if (!currentQuestion) return <div>No question data available</div>;

  const selectedOption = selectedAnswers[currentQuestionIndex];
  const isCorrect = selectedOption === currentQuestion.correctAnswer;
  const progress = ((currentQuestionIndex + 1) / quiz.questions.length) * 100;

  // عدد الإجابات الصحيحة
  const correctAnswersCount = calculateCorrectAnswers();

  const renderQuestion = (question, index) => {
  const isReview = reviewMode || quizFinished;
  const selectedOption = selectedAnswers[index];
  const correctAnswer = question.correctAnswer;
  const isAnswered = selectedOption !== undefined;

  return (
    <div>
      <div className="question-text">
        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{question.question}</pre>
      </div>
      <ul className="options-list">
        {question.options.map((option, optIndex) => {
          const isCorrect = optIndex === correctAnswer;
          const isSelected = optIndex === selectedOption;
          const isWrongSelected = isSelected && !isCorrect;

          return (
            <li
              key={optIndex}
              className={`option-item 
                ${(isAnswered || isReview) && isCorrect ? 'correct-answer' : ''}
                ${isWrongSelected ? 'wrong-answer' : ''}
              `}
              onClick={() => !isReview && handleAnswerSelection(index, optIndex)}
            >
              {option}
              {(isAnswered || isReview) && isCorrect && (
                <span className="correct-indicator"> </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
  return (
    <div className="quiz-container">
      {!quizFinished ? (
        <>
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>
          <h2 className="quiz-title">{quiz.title}</h2>
          <p className="quiz-date">
            Time: {timeLeft} | Correct Answers: {correctAnswersCount}
          </p>
          <div className="questions-list">
            <div
              key={currentQuestion.id || currentQuestionIndex}
              className="question-card"
              ref={(el) => (questionRefs.current[currentQuestionIndex] = el)}
            >
              <div className="question-header">
                <h3 className="question-number">Question {currentQuestionIndex + 1}</h3>
              </div>
              <div className="question-content">
                {renderQuestion(currentQuestion, currentQuestionIndex)}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="quiz-result">
          <h2>Quiz Finished!</h2>
          <p>
            Your score: {score} out of {quiz.questions.length}
          </p>
          {!reviewMode ? (
            <>
              <button className="Btn" onClick={handleReview}>Review Questions</button>
              <button className="Btn" onClick={handleRetakeQuiz}>Retake Quiz</button>
            </>
          ) : (
            <>
              <div className="questions-list">
                {quiz.questions.map((question, index) => (
                  <div
                    key={question.id || index}
                    className="question-card"
                    ref={(el) => (questionRefs.current[index] = el)}
                  >
                    <div className="question-header">
                      <h3 className="question-number">Question {index + 1}</h3>
                    </div>
                    <div className="question-content">
                      {renderQuestion(question, index)}
                    </div>
                  </div>
                ))}
              </div>
              <button className="Btn" onClick={handleReview}>Back to Results</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}